/**
 * Plan Configuration — Frontend Single Source of Truth
 *
 * THIS IS THE ONLY FRONTEND FILE to update when changing plan names, prices,
 * budgets, or limits. All UI components import from here.
 *
 * Schema versioning:
 *   PLAN_SCHEMAS holds all billing schema versions (v3, v4, ...).
 *   ACTIVE_SCHEMA is the version assigned to new subscribers.
 *   PLANS is an alias for PLAN_SCHEMAS[ACTIVE_SCHEMA] — unchanged for backward compat.
 *
 *   To ship v4:
 *     1. Add 'v4' entry to PLAN_SCHEMAS with new plan definitions.
 *     2. Change ACTIVE_SCHEMA to 'v4'.
 *     3. Existing v3 subscribers are unaffected — their SubscriptionSection reads
 *        their org's billing_schema_version to look up current plan details.
 *
 * Backend counterpart: backend/config/constants.py + shared/plans.json
 */

// ─────────────────────────────────────────────────────────────────────────────
// Plan IDs
// ─────────────────────────────────────────────────────────────────────────────

export const PLAN_IDS = {
  FREE:     'free',
  BASIC:    'basic',
  STANDARD: 'standard',
  PREMIUM:  'premium',
  PAYG:     'payg',
} as const;

export type PlanId = typeof PLAN_IDS[keyof typeof PLAN_IDS];

/**
 * Maps legacy plan names (old schemas) to their v3 canonical IDs.
 * plus → basic, ultra → standard, pro → premium
 */
export const LEGACY_PLAN_MAP: Record<string, PlanId> = {
  plus:  'basic',
  ultra: 'standard',
  pro:   'premium',
};

// ─────────────────────────────────────────────────────────────────────────────
// Plan definition shape
// ─────────────────────────────────────────────────────────────────────────────

export interface PlanConfig {
  id: PlanId;
  displayName: string;
  /** $/month subscription price. 0 for free and payg. */
  price: number;
  /** River-rate AI usage dollars per month. null means metered (payg). */
  aiUsageBudget: number | null;
  aiUsageBudgetType: 'lifetime' | 'monthly' | 'metered';
  /** Determines upgrade direction. Higher = more capable. */
  rank: number;
  limits: {
    /** Max projects/spaces. -1 = unlimited. */
    projects: number;
    /** Monthly image generations. 0 = none, -1 = unlimited. */
    imageGenerations: number;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Schema versioning
// ─────────────────────────────────────────────────────────────────────────────

/** Current active billing schema. New orgs are assigned this version. */
export const ACTIVE_SCHEMA = 'v3';

/**
 * All billing schema versions, keyed by version string.
 * When adding a new billing generation (v4, v5, ...), add it here without
 * touching existing entries — existing subscribers stay on their schema.
 */
export const PLAN_SCHEMAS: Record<string, Record<PlanId, PlanConfig>> = {
  v3: {
    free: {
      id:                 'free',
      displayName:        'Free',
      price:              0,
      aiUsageBudget:      0.25,
      aiUsageBudgetType:  'lifetime',
      rank:               0,
      limits: { projects: -1, imageGenerations: 0 },
    },
    basic: {
      id:                 'basic',
      displayName:        'Basic',
      price:              14,
      aiUsageBudget:      15,
      aiUsageBudgetType:  'monthly',
      rank:               1,
      limits: { projects: -1, imageGenerations: -1 },
    },
    standard: {
      id:                 'standard',
      displayName:        'Standard',
      price:              36,
      aiUsageBudget:      45,
      aiUsageBudgetType:  'monthly',
      rank:               2,
      limits: { projects: -1, imageGenerations: -1 },
    },
    premium: {
      id:                 'premium',
      displayName:        'Premium',
      price:              100,
      aiUsageBudget:      140,
      aiUsageBudgetType:  'monthly',
      rank:               3,
      limits: { projects: -1, imageGenerations: -1 },
    },
    payg: {
      id:                 'payg',
      displayName:        'Pay As You Go',
      price:              0,
      aiUsageBudget:      null,
      aiUsageBudgetType:  'metered',
      // rank: 99 keeps PAYG out of the subscription tier ladder
      // (free=0, basic=1, standard=2, premium=3). getNextPlan filters it.
      rank:               99,
      limits: { projects: -1, imageGenerations: -1 },
    },
  },
};

/**
 * Return all plan configs for a schema version.
 * Falls back to ACTIVE_SCHEMA if the version is unknown.
 */
export function getPlansForSchema(schemaVersion: string = ACTIVE_SCHEMA): Record<string, PlanConfig> {
  return PLAN_SCHEMAS[schemaVersion] ?? PLAN_SCHEMAS[ACTIVE_SCHEMA];
}

/**
 * Return the PlanConfig for a specific plan within a schema version.
 * Resolves legacy aliases (plus → basic, etc.) before looking up.
 * Falls back to the free plan if the plan ID is unknown.
 */
export function getPlanForSchema(planId: string, schemaVersion: string = ACTIVE_SCHEMA): PlanConfig {
  const schema = getPlansForSchema(schemaVersion);
  const normalized = normalizePlanId(planId);
  return schema[normalized] ?? (PLAN_SCHEMAS[ACTIVE_SCHEMA].free as PlanConfig);
}

// ─────────────────────────────────────────────────────────────────────────────
// Backward-compatible aliases — unchanged for all existing callers
// ─────────────────────────────────────────────────────────────────────────────

/** All plans for the active schema. Backward-compatible alias. */
export const PLANS: Record<PlanId, PlanConfig> = PLAN_SCHEMAS[ACTIVE_SCHEMA];

// ─────────────────────────────────────────────────────────────────────────────
// Derived lookups — auto-computed, never manually maintained
// ─────────────────────────────────────────────────────────────────────────────

/** Plan rank by ID, including legacy name aliases. */
export const PLAN_RANKS: Record<string, number> = {
  ...Object.fromEntries(Object.values(PLANS).map(p => [p.id, p.rank])),
  ...Object.fromEntries(Object.entries(LEGACY_PLAN_MAP).map(([old, cur]) => [old, PLANS[cur].rank])),
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalize any plan string (legacy or current) to a valid PlanId.
 * Unknown strings fall back to 'free'.
 */
export function normalizePlanId(plan: string): PlanId {
  if (plan in PLANS) return plan as PlanId;
  if (plan in LEGACY_PLAN_MAP) return LEGACY_PLAN_MAP[plan];
  return 'free';
}

/**
 * Get the full PlanConfig for a given plan ID, handling legacy names.
 * Uses the active schema. Unknown IDs fall back to the free plan config.
 */
export function getPlan(planId: string): PlanConfig {
  return PLANS[normalizePlanId(planId)];
}

/**
 * Get the next upgrade plan above the given plan, or null if already at top.
 */
export function getNextPlan(planId: string): PlanConfig | null {
  const current = getPlan(planId);
  const candidates = Object.values(PLANS)
    // Exclude PAYG from the subscription upgrade path — it's a parallel billing
    // mode, not a tier above any subscription plan.
    .filter(p => p.rank === current.rank + 1 && p.id !== 'payg')
    .sort((a, b) => a.rank - b.rank);
  return candidates[0] ?? null;
}

/**
 * Human-readable display name for the required plan (for upgrade prompts).
 * e.g. getUpgradeDisplayName('basic') → 'Basic'
 */
export function getUpgradeDisplayName(planId: string): string {
  return getPlan(planId).displayName;
}
